import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { type Control, type UseFormReset, useWatch } from 'react-hook-form';
import { SHOP_CATEGORY_STORAGE_KEY } from '@/constants';
import type { ShopCategoryForm } from '@/types';
import { getFormDataFromLocalStorage, saveFormDataToLocalStorage } from '@/utils';
import { createEmptyAttributeCriteriaGroup, normalizeAttributeCriteriaGroups } from '@/utils/attributeCriteria';
export const createEmptyShopCategoryValues = (): ShopCategoryForm => ({
  categoryKey: '',
  categoryPath: '',
  market: null,
  url: '',
  startDate: '',
  endDate: '',
  attributeCriteriaGroups: [createEmptyAttributeCriteriaGroup()],
  includedWebstyles: '',
  excludedWebstyles: ''
});
export const emptyShopCategoryValues: ShopCategoryForm = createEmptyShopCategoryValues();
export const getInitialShopCategoryValues = (): ShopCategoryForm => {
  const storedValues = getFormDataFromLocalStorage<ShopCategoryForm>(SHOP_CATEGORY_STORAGE_KEY);
  if (!storedValues) {
    return createEmptyShopCategoryValues();
  }
  return {
    ...createEmptyShopCategoryValues(),
    ...storedValues,
    attributeCriteriaGroups: normalizeAttributeCriteriaGroups(storedValues.attributeCriteriaGroups)
  };
};
export const useShopCategoryFormPersistence = (control, reset) => {
  const $ = _c(12);
  let t0;
  if ($[0] !== control) {
    t0 = {
      control
    };
    $[0] = control;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const values = useWatch(t0);
  const skipSaveRef = useRef(true);
  let t1;
  let t2;
  if ($[2] !== reset) {
    t1 = () => {
      reset(getInitialShopCategoryValues());
    };
    t2 = [reset];
    $[2] = reset;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== values) {
    t3 = () => {
      if (skipSaveRef.current) {
        skipSaveRef.current = false;
        return;
      }
      saveFormDataToLocalStorage(SHOP_CATEGORY_STORAGE_KEY, {
        categoryKey: values.categoryKey ?? "",
        categoryPath: values.categoryPath ?? "",
        market: values.market ?? null,
        url: values.url ?? "",
        startDate: values.startDate ?? "",
        endDate: values.endDate ?? "",
        attributeCriteriaGroups: normalizeAttributeCriteriaGroups(values.attributeCriteriaGroups),
        includedWebstyles: values.includedWebstyles ?? "",
        excludedWebstyles: values.excludedWebstyles ?? ""
      });
    };
    t4 = [values];
    $[5] = values;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== reset) {
    t5 = () => {
      skipSaveRef.current = true;
      ;
      try {
        localStorage.removeItem(SHOP_CATEGORY_STORAGE_KEY);
      } catch (t6) {
        const error = t6;
        console.error("Error clearing shop category form data from localStorage", error);
      }
      reset(createEmptyShopCategoryValues());
    };
    $[8] = reset;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const clearForm = t5;
  let t6;
  if ($[10] !== clearForm) {
    t6 = {
      clearForm
    };
    $[10] = clearForm;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};